import { Injectable, OnDestroy } from '@angular/core';

/**
 * Keeps Bootstrap dropdown menus inside a `.table-responsive` from being clipped by it.
 *
 * `.table-responsive` is `overflow: auto` on both axes (tables.scss), so an absolutely positioned
 * menu opened in the last visible row is cut off at the container's edge and reads as rendering
 * "behind" the row below. The fix is to move the menu to `<body>` for as long as it is open, which
 * takes it out of every clipping ancestor; `body > .dropdown-menu.show` in tables.scss then gives
 * it the right z-index layer.
 *
 * This listens once on `document` rather than per table. Bootstrap fires `show.bs.dropdown` and
 * `hidden.bs.dropdown` on the toggle and they bubble, so a single pair of listeners covers every
 * dropdown in the app, including ones added later — nothing has to be imported or remembered at the
 * call site. Previously this lived on ResponsiveTableDirective, which meant a list built without
 * that directive silently went back to being clipped.
 *
 * Scope is deliberately `.table-responsive` and not "every dropdown". A portaled menu keeps its own
 * component styles (Angular's emulated encapsulation scopes by an attribute on the element, which
 * travels with it) but loses any rule written as an ancestor-descendant pair. Several of those
 * exist — `.navbar .dropdown-menu`, `ms-*-search-filter .dropdown-menu`, the organization
 * switcher's `:host ::ng-deep`, and the document tiles' `&:has(.dropdown-menu.show)` — and none of
 * those dropdowns sit inside a `.table-responsive`, so confining the portal to it leaves them
 * untouched.
 */
@Injectable({ providedIn: 'root' })
export class DropdownPortalService implements OnDestroy {
    private static readonly CLIPPING_CONTAINER_SELECTOR = '.table-responsive';
    private static readonly TOGGLE_SELECTOR = '[data-bs-toggle="dropdown"]';
    private static readonly OPEN_TOGGLE_SELECTOR = '[data-bs-toggle="dropdown"][aria-expanded="true"]';
    private static readonly DROPDOWN_SELECTOR = '.dropdown';
    private static readonly MENU_SELECTOR = ':scope > .dropdown-menu';
    private static readonly MODAL_SELECTOR = '.modal';
    // Raises a portaled menu over its own modal — see tables.scss.
    private static readonly MENU_INSIDE_MODAL_CLASS = 'ms-dropdown-menu-in-modal';

    private portaledMenu?: { menu: HTMLElement; parent: HTMLElement; owner: HTMLElement; nextSibling: Node | null };
    private scrollHandler?: () => void;
    private showHandler?: (event: Event) => void;
    private hiddenHandler?: () => void;
    private isStarted = false;

    /** Called once from provideAppInitializer. Idempotent, so a second call is harmless. */
    public start(): void {
        if (this.isStarted) return;
        this.isStarted = true;

        this.showHandler = event => this.onDropdownShow(event);
        this.hiddenHandler = () => this.onDropdownHidden();

        document.addEventListener('show.bs.dropdown', this.showHandler);
        document.addEventListener('hidden.bs.dropdown', this.hiddenHandler);
    }

    /** The listeners are on `document`, which outlives this instance — a test creates one per TestBed. */
    public ngOnDestroy(): void {
        if (this.showHandler) document.removeEventListener('show.bs.dropdown', this.showHandler);
        if (this.hiddenHandler) document.removeEventListener('hidden.bs.dropdown', this.hiddenHandler);

        this.stopListeningForScroll();
        this.returnMenuFromBody();

        this.showHandler = undefined;
        this.hiddenHandler = undefined;
        this.isStarted = false;
    }

    /**
     * Brings a still-open menu home before the view that owns it is destroyed, so it is not left
     * behind on <body>. `scope` limits that to the list the menu came from — a second list on screen
     * may have its own open. Called from ResponsiveTableDirective.ngOnDestroy.
     *
     * Matched against the clipping container captured when the menu was portaled, not against its
     * row: an `@for` refresh can detach that row while the menu is still open.
     */
    public release(scope: HTMLElement): void {
        if (!this.portaledMenu?.owner.contains(scope)) return;

        this.stopListeningForScroll();
        this.returnMenuFromBody();
    }

    /**
     * Sweeps a menu left on <body> by a page that unmounted without closing it. ResponsiveTableDirective
     * covers the lists that use it; this covers a `.table-responsive` that does not, which the portal
     * still serves. Only acts on a true orphan, so a menu open on the page now is left alone.
     */
    public releaseOrphaned(): void {
        if (!this.portaledMenu || this.portaledMenu.owner.isConnected) return;

        this.stopListeningForScroll();
        this.returnMenuFromBody();
    }

    private onDropdownShow(event: Event): void {
        const toggle = this.resolveToggle(event);
        if (!toggle?.closest(DropdownPortalService.CLIPPING_CONTAINER_SELECTOR)) return;

        this.closeOtherDropdowns(toggle);
        this.portalMenuToBody(toggle);
        this.listenForScroll(toggle);
    }

    private onDropdownHidden(): void {
        this.stopListeningForScroll();
        this.returnMenuFromBody();
    }

    // Bootstrap dispatches the event on the toggle and also hands it back as relatedTarget; take
    // whichever is actually a toggle so this does not depend on which one Bootstrap favours.
    private resolveToggle(event: Event): HTMLElement | undefined {
        const candidates = [(event as unknown as { relatedTarget?: unknown }).relatedTarget, event.target];

        return candidates.find((candidate): candidate is HTMLElement =>
            candidate instanceof HTMLElement && candidate.matches(DropdownPortalService.TOGGLE_SELECTOR));
    }

    /**
     * Moves the open menu to document.body so it escapes every clipping ancestor, remembering where
     * it came from so it can be put back.
     */
    private portalMenuToBody(toggle: HTMLElement): void {
        this.returnMenuFromBody();

        const parent = toggle.closest(DropdownPortalService.DROPDOWN_SELECTOR) as HTMLElement | null;
        if (!parent) return;

        const menu = parent.querySelector(DropdownPortalService.MENU_SELECTOR) as HTMLElement | null;
        if (!menu) return;

        const owner = toggle.closest(DropdownPortalService.CLIPPING_CONTAINER_SELECTOR) as HTMLElement | null;
        if (!owner) return;

        this.portaledMenu = { menu: menu, parent: parent, owner: owner, nextSibling: menu.nextSibling };

        if (toggle.closest(DropdownPortalService.MODAL_SELECTOR)) {
            menu.classList.add(DropdownPortalService.MENU_INSIDE_MODAL_CLASS);
        }

        document.body.appendChild(menu);
    }

    private returnMenuFromBody(): void {
        if (!this.portaledMenu) return;

        const { menu, parent, nextSibling } = this.portaledMenu;
        menu.classList.remove(DropdownPortalService.MENU_INSIDE_MODAL_CLASS);

        // Re-parenting to a row that has since been re-rendered away is still the right move: it
        // takes the orphan off <body>, and the detached subtree is collected with its row.
        if (nextSibling && nextSibling.parentNode === parent) {
            parent.insertBefore(menu, nextSibling);
        } else {
            parent.appendChild(menu);
        }

        this.portaledMenu = undefined;
    }

    /**
     * A portaled menu is positioned once, at open. Scrolling the container underneath it would
     * leave it pointing at whichever row has moved into that spot — the "wrong account" bug — so
     * close it instead of trying to track the row. Capture phase, because scroll does not bubble.
     */
    private listenForScroll(toggle: HTMLElement): void {
        this.stopListeningForScroll();

        this.scrollHandler = () => this.hideDropdown(toggle);
        document.addEventListener('scroll', this.scrollHandler, { capture: true, passive: true });
    }

    private stopListeningForScroll(): void {
        if (!this.scrollHandler) return;

        document.removeEventListener('scroll', this.scrollHandler, { capture: true });
        this.scrollHandler = undefined;
    }

    /**
     * Closes any other open dropdown. Uses Bootstrap's own hide() rather than a click elsewhere,
     * which would scroll the page to the element it landed on.
     */
    private closeOtherDropdowns(openingToggle: HTMLElement): void {
        const openToggles = document.querySelectorAll(DropdownPortalService.OPEN_TOGGLE_SELECTOR);

        openToggles.forEach(toggle => {
            if (toggle !== openingToggle) this.hideDropdown(toggle as HTMLElement);
        });
    }

    private hideDropdown(toggle: HTMLElement): void {
        const bootstrapGlobal = (window as { bootstrap?: { Dropdown?: { getInstance(element: HTMLElement): { hide(): void } | null } } }).bootstrap;
        bootstrapGlobal?.Dropdown?.getInstance(toggle)?.hide();
    }
}
